/**
 * Retorna o valor padrão para um determinado tipo de campo
 * @param type - Tipo do campo (string, number, boolean, etc)
 * @returns Valor padrão apropriado para o tipo
 */
export const getDefaultValueForType = (type: string): string | number | boolean | Record<string, unknown> => {
  switch (type?.toLowerCase()) {
    case 'string':
    case 'text':
    case 'file':
      return '';
    case 'number':
    case 'integer':
      return 0;
    case 'boolean':
      return false;
    case 'json':
      return '{}';
    case 'object':
      return {};
    default:
      return '';
  }
};

/**
 * Extrai campos das configurações do nó
 * @param settings - Configurações do nó
 * @returns Array de campos extraídos
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const extractFields = (settings: Record<string, any>): Record<string, unknown>[] => {
  if (!settings) return [];

  if (Array.isArray(settings.fields)) {
    return settings.fields;
  }

  if (Array.isArray(settings.tabs)) {
    return settings.tabs.flatMap((tab: Record<string, unknown>) => (tab.fields as Record<string, unknown>[]) || []);
  }

  if (settings.configSchema?.properties) {
    return Object.entries(settings.configSchema.properties as Record<string, unknown>).map(([key, prop]: [string, unknown]) => {
      const p = prop as Record<string, unknown>;
      return ({
        name: key,
        type: p.type,
        label: p.title || key,
        description: p.description,
        required: (settings.configSchema.required as string[] | undefined)?.includes(key),
        options: p.enum,
        default: p.default
      });
    });
  }

  return [];
};

/**
 * Formata um nó para o schema do workflow
 * @param node - Nó do ReactFlow
 * @returns Nó formatado para o schema
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const formatNodeForSchema = (node: Record<string, any>) => {
  // Criar cópia do data excluindo propriedades temporárias de teste
  const { status, testOutput, loading, webhookStatus, webhookProcessed, ...cleanData } = node.data;
  
  return {
    id: node.id,
    nodeId: node.data.nodeId,
    name: node.data.name || node.data.type,
    type: node.data.type,
    category: node.data.category,
    title: node.data.title || node.data.label,
    label: node.data.label,
    description: node.data.description,
    position: node.position,
    config: node.config,
    data: {
      ...cleanData,
      title: node.data.title || node.data.label,
    },
    settings: node.data.settings,
    logo: node.data.logo_url,
    // NÃO incluir status - é propriedade temporária de teste
  };
};

/**
 * Formata uma aresta para o schema do workflow
 * @param edge - Aresta do ReactFlow
 * @returns Aresta formatada para o schema
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const formatEdgeForSchema = (edge: Record<string, any>) => {
  // Criar cópia do data excluindo propriedades temporárias de teste
  const { success, ...cleanEdgeData } = edge.data || {};

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formattedEdge: Record<string, any> = {
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle,
    targetHandle: edge.targetHandle,
    type: edge.type,
    input: edge.input,
    data: cleanEdgeData
  };

  // Certifica-se de que os dados de sourceHandle e targetHandle também estão presentes em data
  if (edge.data?.sourceHandle) {
    formattedEdge.sourceHandle = edge.data.sourceHandle;
  }

  // Tratamento especial para edges que saem de nós if_then (sourceHandle "true" ou "false")
  if (edge.sourceHandle === 'true' || edge.sourceHandle === 'false') {
    // Adicionar metadata com condição para estas edges
    formattedEdge.metadata = { 
      condition: edge.sourceHandle 
    };
    
    // Adicionar também no data para manter consistência
    if (!formattedEdge.data) formattedEdge.data = {};
    formattedEdge.data.condition = edge.sourceHandle;
    formattedEdge.data.sourceHandle = edge.sourceHandle;
    
    // Para edges "true" ou "false", adicionar o ID do nó de origem para facilitar a referência
    if (edge.source) {
      formattedEdge.data.sourceNodeId = edge.source;
    }
  }
  // Adiciona metadados da aresta se existir uma condição (true/false para nós if_then)
  else if (edge.data?.condition) {
    if (!formattedEdge.data) formattedEdge.data = {};
    formattedEdge.data.condition = edge.data.condition;
    
    // Adiciona também o metadata para ser consistente com o formato do workflow-cleaner
    formattedEdge.metadata = { condition: edge.data.condition };
  }

  return formattedEdge;
};

/**
 * Cria um novo nó para o workflow
 * @param nodeInfo - Informações do nó
 * @param position - Posição do nó
 * @param workflowId - ID do workflow
 * @returns Novo nó formatado
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const createNewNode = (nodeInfo: Record<string, any>, position: { x: number; y: number }, workflowId: string, getId: () => string) => ({
  id: getId(),
  type: 'customNode',
  position,
  data: { 
    label: nodeInfo.name,
    title: nodeInfo.name, 
    description: nodeInfo.description,
    type: nodeInfo.type,
    name: nodeInfo.type, 
    nodeId: nodeInfo.id,
    category: nodeInfo.category,
    logo_url: nodeInfo.logo_url,
    workflowId,
    status: undefined,
    loading: false,
    settings: nodeInfo.settings || {}
  },
  config: nodeInfo.settings?.defaultValues || {}
});

/**
 * Atualiza o status de um nó com base nos dados do webhook
 * @param node - Nó a ser atualizado
 * @param webhookData - Dados do webhook
 * @returns Nó atualizado
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const updateNodeWithWebhookData = (node: Record<string, any>, webhookData: Record<string, any>) => {
  if (node.id === webhookData.nodeId) {
    return {
      ...node,
      data: {
        ...node.data,
        status: webhookData.type === 'completed' ? 'success' : 'error',
        webhookStatus: webhookData.type === 'completed' ? 'success' : 'error',
        testOutput: webhookData.type === 'completed' ? webhookData.data?.body?.data : null
      }
    };
  }
  return node;
};

/**
 * Atualiza uma aresta com dados do webhook
 * @param edge - Aresta a ser atualizada
 * @param webhookData - Dados do webhook
 * @returns Aresta atualizada
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const updateEdgeWithWebhookData = (edge: Record<string, any>, webhookData: Record<string, any>) => {
  if (edge.source === webhookData.nodeId) {
    return {
      ...edge,
      data: {
        ...edge.data,
        input: webhookData.data?.body?.data
      }
    };
  }
  return edge;
};
